import { computed, inject } from '@angular/core';
import { isNotUndefinedOrNull } from '@bizzdesign/bizzdesign-sdk';
import {
  type ContentSelection,
  FilterGlobalStore,
  type SearchResultItem,
  SearchResultsGlobalStore,
} from '@bizzdesign/data-access-search';
import {
  getState,
  patchState,
  signalStore,
  withComputed,
  withHooks,
  withMethods,
  withProps,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { pipe, tap } from 'rxjs';

export type ObjectSearchResultsState = {
  searchResultItems: SearchResultItem[];
  selectedItems: SearchResultItem[];
  unselectedItems: SearchResultItem[];
  previousSelectedItems: SearchResultItem[];
  selectAllApplied: boolean;
  lastToggledItemIndex: number | undefined;
  shiftToggledItemIndex: number | undefined;
};

export const initialObjectSearchResultsState: ObjectSearchResultsState = {
  searchResultItems: [],
  selectedItems: [],
  unselectedItems: [],
  previousSelectedItems: [],
  selectAllApplied: false,
  lastToggledItemIndex: undefined,
  shiftToggledItemIndex: undefined,
};

export const ObjectSearchResultsComponentStore = signalStore(
  withState(initialObjectSearchResultsState),
  withProps(() => {
    return {
      searchResultsGlobalStore: inject(SearchResultsGlobalStore),
      filterGlobalStore: inject(FilterGlobalStore),
    };
  }),
  withComputed(
    ({
      selectedItems,
      selectAllApplied,
      unselectedItems,
      filterGlobalStore,
      searchResultsGlobalStore,
      lastToggledItemIndex,
      shiftToggledItemIndex,
      searchResultItems,
    }) => {
      return {
        numberOfSelectedItems: computed((): number => {
          if (selectAllApplied()) {
            return (
              searchResultsGlobalStore.totalObjectsCount() -
              unselectedItems().length
            );
          } else {
            return selectedItems().length;
          }
        }),
        searchResultsGlobalStoreProperties: computed(() =>
          getState(searchResultsGlobalStore),
        ),
        editDataButtonDisabled: computed(
          () =>
            selectedItems().every((item) => item.externalId) &&
            !selectAllApplied(),
        ),
        selectAllDisabled: computed(() => {
          const objectResultsExcluded =
            filterGlobalStore.advancedFilter().scopeFilters.length !== 0 &&
            !filterGlobalStore.advancedFilter().scopeFilters.includes('OBJECT');
          return (
            searchResultsGlobalStore.totalObjectsCount() === 0 ||
            (selectAllApplied() && unselectedItems().length === 0) ||
            objectResultsExcluded
          );
        }),
        shiftToggledItems: computed(() => {
          const items = searchResultItems();
          const lastToggledItemIndexValue = lastToggledItemIndex();
          const shiftToggledItemIndexValue = shiftToggledItemIndex();

          if (
            isNotUndefinedOrNull(lastToggledItemIndexValue) &&
            isNotUndefinedOrNull(shiftToggledItemIndexValue) &&
            lastToggledItemIndexValue >= 0 &&
            shiftToggledItemIndexValue >= 0 &&
            lastToggledItemIndexValue < items.length &&
            shiftToggledItemIndexValue < items.length
          ) {
            const shiftToggledItemStatus =
              items[shiftToggledItemIndexValue].selected;
            const start = Math.min(
              lastToggledItemIndexValue,
              shiftToggledItemIndexValue,
            );
            const end = Math.max(
              lastToggledItemIndexValue,
              shiftToggledItemIndexValue,
            );
            return items.slice(start + 1, end).filter(
              (item) =>
                !item.disabled &&
                !item.deleted &&
                item.selected === shiftToggledItemStatus,
              // only include items that are not disabled
              // match the toggled status to the shift toggled item, not toggle items based on their current selected status
            );
          }
          return [];
        }),
      };
    },
  ),
  withMethods((store) => {
    return {
      updateSearchResultItems: (searchResultItems: SearchResultItem[]) => {
        const deletedItemIds = new Set(
          searchResultItems
            .filter((item) => item.deleted)
            .map((item) => item.id),
        );

        patchState(store, {
          searchResultItems,
          lastToggledItemIndex: undefined,
          shiftToggledItemIndex: undefined,
        });

        const currentSelectedItems = store
          .selectedItems()
          .filter((item) => !deletedItemIds.has(item.id));

        const newSelectedItems = store.selectAllApplied()
          ? searchResultItems.filter(
              (item) =>
                !item.deleted &&
                !store
                  .unselectedItems()
                  .some((unselected) => unselected.id === item.id),
            )
          : currentSelectedItems.filter((selectedItem) =>
              searchResultItems.some((item) => item.id === selectedItem.id),
            );

        const newSelectedItemsWithoutDuplicates = newSelectedItems.filter(
          (newSelectedItem) =>
            !currentSelectedItems.some(
              (currentSelectedItem) =>
                currentSelectedItem.id === newSelectedItem.id,
            ),
        );

        patchState(store, {
          unselectedItems: [
            ...store.unselectedItems(),
            ...searchResultItems.filter(
              (item) =>
                deletedItemIds.has(item.id) &&
                !store
                  .unselectedItems()
                  .some((unselectedItem) => unselectedItem.id === item.id),
            ),
          ],
          selectedItems: [
            ...currentSelectedItems,
            ...newSelectedItemsWithoutDuplicates,
          ],
        });
      },
      updateToggledItemIndexes: (shiftKey: boolean, itemId: string) => {
        const searchResultItemIndex = store
          .searchResultItems()
          .findIndex((item) => item.id === itemId);
        if (searchResultItemIndex < 0) {
          return;
        }
        if (shiftKey) {
          patchState(store, {
            shiftToggledItemIndex: searchResultItemIndex,
          });
        } else {
          patchState(store, {
            lastToggledItemIndex: searchResultItemIndex,
            shiftToggledItemIndex: undefined,
          });
        }
      },
      updateSelectedItems: (selectedItems: SearchResultItem[]) => {
        // if there are shiftToggledItems, we need to add/remove them from the selectedItems
        if (store.shiftToggledItems().length > 0) {
          const selectedItemsIds = new Set(selectedItems.map((x) => x.id));
          const shiftToggledItemsIds = new Set(
            store.shiftToggledItems().map((x) => x.id),
          );
          const filteredSelectedItems = selectedItems.filter(
            (x) => !shiftToggledItemsIds.has(x.id),
          );
          const toAdd = store
            .shiftToggledItems()
            .filter((x) => !selectedItemsIds.has(x.id));
          selectedItems = [...filteredSelectedItems, ...toAdd];
        }

        // after updating selected items, set lastToggledItemIndex to the shiftToggledItemIndex
        // if defined, it should serve as the new base for further shift selections
        if (isNotUndefinedOrNull(store.shiftToggledItemIndex())) {
          patchState(store, {
            lastToggledItemIndex: store.shiftToggledItemIndex(),
          });
        }

        patchState(store, {
          selectedItems,
        });
      },
      applySelectAll: () => {
        const newSelectedItems = store
          .searchResultItems()
          .filter((item) => !item.disabled && !item.deleted);

        patchState(store, {
          selectAllApplied: true,
          unselectedItems: [],
          selectedItems: newSelectedItems,
        });
      },
      cancelSelection: () => {
        store.searchResultsGlobalStore.updateIsSelectionMode(false);
      },
      getObjectSelection: (): ContentSelection => {
        const selectedItems = store.selectedItems();
        const hasObjectSelection =
          selectedItems.length > 0 || store.selectAllApplied();

        return {
          selectedItems,
          fetchAllItems: store.selectAllApplied(),
          unselectedItems: store.unselectedItems(),
          selectedItemsCategory: hasObjectSelection ? 'object' : undefined,
          selectedItemsCount: hasObjectSelection
            ? store.numberOfSelectedItems()
            : 0,
        };
      },
      updateUnselectedItemsFromCurrentSelection: () => {
        const newUnselectedItems = store
          .previousSelectedItems()
          .filter(
            (item) =>
              !store
                .selectedItems()
                .some((selectedItem) => selectedItem.id === item.id),
          );
        const newSelectedItems = store
          .selectedItems()
          .filter(
            (item) =>
              !store
                .previousSelectedItems()
                .some((prevItem) => prevItem.id === item.id),
          );

        patchState(store, {
          unselectedItems: [
            ...store
              .unselectedItems()
              .filter(
                (item) =>
                  !newSelectedItems.some((newItem) => newItem.id === item.id),
              ),
            ...newUnselectedItems,
          ],
        });
      },
      updatePreviousSelectedItems: rxMethod<SearchResultItem[]>(
        pipe(
          tap(() => {
            patchState(store, {
              previousSelectedItems: store.selectedItems(),
            });
          }),
        ),
      ),
      updateSearchResultItemsAfterSelection: rxMethod<SearchResultItem[]>(
        pipe(
          tap(() => {
            const storeSearchResultItems = store.searchResultItems();

            storeSearchResultItems.forEach((item) => {
              item.selected = store
                .selectedItems()
                .some((selectedItem) => item.id === selectedItem.id);
            });

            patchState(store, {
              searchResultItems: storeSearchResultItems,
            });
          }),
        ),
      ),
      updateIsSelectionModeAfterSelection: rxMethod<number>(
        pipe(
          tap(() => {
            store.searchResultsGlobalStore.updateIsSelectionMode(
              store.numberOfSelectedItems() > 0,
            );
          }),
        ),
      ),
      resetSelectionStates: rxMethod<boolean>(
        pipe(
          tap((isSelectionMode) => {
            if (!isSelectionMode) {
              patchState(store, {
                selectedItems: [],
                selectAllApplied: false,
                unselectedItems: [],
                previousSelectedItems: [],
                lastToggledItemIndex: undefined,
                shiftToggledItemIndex: undefined,
              });
            }
          }),
        ),
      ),
    };
  }),
  withHooks((store) => {
    return {
      onInit() {
        store.updatePreviousSelectedItems(store.selectedItems);
        store.updateSearchResultItemsAfterSelection(store.selectedItems);
        store.updateIsSelectionModeAfterSelection(store.numberOfSelectedItems);
        store.resetSelectionStates(
          store.searchResultsGlobalStore.isSelectionMode,
        );
        store.searchResultsGlobalStore.updateEditDataLoading(false);
      },
    };
  }),
);

export type ObjectSearchResultsComponentStore = InstanceType<
  typeof ObjectSearchResultsComponentStore
>;
